import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { signupPageModuleQuery } from '@data/sanity/queries/page'
import { SanitySignupPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getPageSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getSignupPage } from '@lib/sanity/page'
import { Locale } from '@lib/language'
import { CustomStaticPropsContext } from '@lib/routes'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface SignupPageProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  page: SanitySignupPage
  site: SanitySiteFragment
}

const SignupPage = ({
  draftMode,
  draftToken,
  locale,
  page,
  site,
}: SignupPageProps) => {
  if (draftMode && draftToken) {
    return (
      <Layout page={page} site={site}>
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={signupPageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
            }}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  return (
    <Layout page={page} site={site}>
      <Modules modules={page.modules} />
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext
): Promise<GetStaticPropsResult<SignupPageProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode
  )
  const signupPage = await getSignupPage(sanityClient, context.locale)

  if (!signupPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      page: signupPage.page,
      site: signupPage.site,
    },
    revalidate: 60,
  }
}

export default SignupPage
